import { useMutation, useQueryClient } from "react-query";

import { createGoal } from "api/goals";
import type { Goal } from "api/types/aba";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_LIST_OF_GOALS } from "@constants/reactQueryKeys";

interface CreateGoalPayload {
  clientId: string;
  goalData: Partial<Goal>;
}

/**
 * Custom hook to create a new goal for a client.
 *
 * @param options - Optional configuration for success and error handling.
 * @returns An object containing the createGoalAction function, loading state, and other mutation info.
 */
const useCreateGoal = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: createGoalAction,
    isLoading: isCreatingGoal,
    ...rest
  } = useMutation(({ clientId, goalData }: CreateGoalPayload) => createGoal(clientId, goalData), {
    onSuccess: (response, variables) => {
      showToast({ message: "Goal created successfully" });
      queryClient.invalidateQueries([GET_LIST_OF_GOALS, variables.clientId]);
      onSuccess(response, variables);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || "Failed to create goal";
      showToast({ message: errorMessage, errorState: true });
      onError(error);
    },
    ...otherOptions,
  });

  return { createGoalAction, isCreatingGoal, ...rest };
};

export default useCreateGoal;
